import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LogoDark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="mb-8"><LogoDark /></div>
        {sent ? (
          <div data-testid="forgot-confirmation" className="text-center space-y-3">
            <div className="h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
              <MailCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Check your inbox</h1>
            <p className="text-sm text-slate-500">
              If that email is registered, a reset link has been sent. It expires in 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-700 hover:text-cyan-900 pt-2">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900">Reset password</h1>
            <p className="text-slate-500 mt-1 mb-6 text-sm">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="forgot-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required className="h-11" placeholder="you@clinic.com" />
              </div>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <Button data-testid="forgot-submit-button" type="submit" disabled={loading}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-700 hover:text-cyan-900 mt-6">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
